import React, { useEffect, useState } from "react";
import axios from "axios";
import logo from "../../assets/logo.png";
import { useAuthContext } from "../../hooks/useAuthContext";
import { ApiConnectionString } from "../../constants/ApiConntection";
import { Link } from "react-router-dom";

export default function LoginModal({ cancelModal }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [isForgotPassword, setIsForgotPassword] = useState(false);
  const [forgotLoading, setForgotLoading] = useState(false);
  const [forgotMessage, setForgotMessage] = useState("");
  const [resetRequested, setResetRequested] = useState(false);
  const { dispatch, user } = useAuthContext();

  useEffect(() => {
    if (user) {
      cancelModal();
    }
  }, [user]);

  const handleLogin = async (e) => {
    e.preventDefault();
    if (!email || !password) return;

    setLoading(true);
    setError("");

    const loginDetails = { email, password };

    axios
      .post(`${ApiConnectionString}/api/login/`, loginDetails)
      .then((res) => {
        if (res.status === 200) {
          dispatch({ type: "LOGIN", payload: res.data });
          cancelModal();
        }
      })
      .catch((err) => {
        if (err.response && err.response.status === 400) {
          setError(err.response.data[0]);
        }
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const handleForgotPassword = (e) => {
    e.preventDefault();
    if (!email) {
      setError("Please enter your email");
      return;
    }

    if (resetRequested) return;

    setForgotLoading(true);
    setError("");
    setForgotMessage("");

    axios
      .post(
        `${ApiConnectionString}/api/ForgotPassword?email=${encodeURIComponent(
          email
        )}`
      )
      .then(() => {
        setForgotMessage(
          "If that email exists we have sent reset instructions"
        );
        setResetRequested(true);
      })
      .catch((err) => {
        if (err.response && err.response.data) {
          setError(err.response.data[0] || "Something went wrong");
        } else {
          setError("Something went wrong");
        }
      })
      .finally(() => {
        setForgotLoading(false);
      });
  };

  return (
    <>
      <div
        className="modal-backdrop show"
        style={{ backdropFilter: "blur(8px)" }}
      ></div>

      <div className="modal show d-block" tabIndex="-1" role="dialog" aria-modal="true">
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content border-0 shadow">
            <div className="modal-header position-relative flex-column border-0">
              <button
                type="button"
                className="btn-close position-absolute top-0 end-0 m-3"
                onClick={cancelModal}
              ></button>

              <div className="text-center w-100 mt-2">
                <img
                  src={logo}
                  alt="logo"
                  title="logo"
                  className="img-fluid"
                  style={{ maxWidth: "200px" }}
                />
                <p className="mt-2 mb-0 brand-dark-purple">
                  {isForgotPassword
                    ? "Reset your password"
                    : "Log in to Border City Software to continue"}
                </p>
              </div>
            </div>

            <div className="modal-body">
              {!isForgotPassword && (
                <form onSubmit={handleLogin}>
                  <div className="mb-3">
                    <label className="form-label brand-purple">Email</label>
                    <input
                      type="email"
                      className="form-control"
                      placeholder="you@example.com"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                    />
                  </div>

                  <div className="mb-3">
                    <label className="form-label brand-purple">Password</label>
                    <div className="input-group">
                      <input
                        type={showPassword ? "text" : "password"}
                        className="form-control"
                        placeholder="Your password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        required
                      />
                      <button
                        type="button"
                        className="btn brand-purple-outline"
                        onClick={() => setShowPassword((prev) => !prev)}
                      >
                        {showPassword ? "Hide" : "Show"}
                      </button>
                    </div>
                  </div>

                  {error && <div className="alert alert-danger py-2">{error}</div>}

                  <div className="d-flex justify-content-between align-items-center mb-4">
                    <div className="form-check">
                      <input className="form-check-input" type="checkbox" id="rememberMe" />
                      <label className="form-check-label" htmlFor="rememberMe">
                        Remember me
                      </label>
                    </div>

                    <button
                      type="button"
                      className="btn btn-link p-0 text-decoration-none brand-purple"
                      onClick={() => {
                        setError("");
                        setForgotMessage("");
                        setResetRequested(false);
                        setIsForgotPassword(true);
                      }}
                    >
                      Forgot password
                    </button>
                  </div>

                  <button
                    type="submit"
                    className="btn brand-purple-inline w-100"
                    disabled={loading}
                  >
                    {loading ? "Logging in..." : "Login"}
                  </button>
                </form>
              )}

              {isForgotPassword && (
                <form onSubmit={handleForgotPassword}>
                  <div className="mb-3">
                    <label className="form-label brand-purple">Email</label>
                    <input
                      type="email"
                      className="form-control"
                      placeholder="you@example.com"
                      value={email}
                      onChange={(e) => {
                        setEmail(e.target.value);
                        setResetRequested(false);
                      }}
                      required
                    />
                  </div>

                  {error && <div className="alert alert-danger py-2">{error}</div>}

                  {forgotMessage && (
                    <div className="alert alert-success py-2">
                      {forgotMessage}
                    </div>
                  )}

                  <div className="d-flex justify-content-between align-items-center mb-4">
                    <button
                      type="button"
                      className="btn btn-link p-0 text-decoration-none brand-purple"
                      onClick={() => {
                        setError("");
                        setForgotMessage("");
                        setIsForgotPassword(false);
                      }}
                    >
                      Back to login
                    </button>
                  </div>

                  <button
                    type="submit"
                    className="btn brand-purple-inline w-100"
                    disabled={forgotLoading || resetRequested}
                  >
                    {forgotLoading
                      ? "Sending..."
                      : resetRequested
                      ? "Reset link sent"
                      : "Send reset link"}
                  </button>
                </form>
              )}
            </div>

            {!isForgotPassword && (
              <div className="modal-footer justify-content-center">
                <span className="text-muted">Do not have an account</span>
                <Link to={"/SignUp"} className="ms-2 text-decoration-none brand-purple">
                  Sign up
                </Link>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
}
